import * as React from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { AutoComplete } from '@/components/ui/auto-complete';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

interface FormAutoCompleteFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  options: string[];
  placeholder?: string;
  description?: React.ReactNode;
  required?: boolean;
  className?: string;
}

interface AutoCompleteInnerProps {
  items: { value: string; label: string }[];
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
}

function AutoCompleteInner({ items, value, onChange, placeholder }: AutoCompleteInnerProps) {
  const [search, setSearch] = React.useState(value);

  React.useEffect(() => {
    setSearch(value);
  }, [value]);

  const filtered = items.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <AutoComplete
      placeholder={placeholder}
      value={search}
      onInputChange={setSearch}
      selectedItem={value ? { value, label: value } : null}
      keyFn={(item) => item.value}
      displayFn={(item) => item.label}
      onSelect={(item) => {
        if (item) {
          onChange(item.value);
          setSearch(item.label);
        } else {
          onChange('');
          setSearch('');
        }
      }}
      filterFn={() => filtered}
      itemToString={(item) => (item ? item.label : '')}
      mode="local"
    />
  );
}

/**
 * Single-select autocomplete over a list of string options. Prefer this over
 * `FormSelectField` when option counts are large (>5) and users benefit from
 * type-ahead filtering.
 */
export const FormAutoCompleteField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  options,
  placeholder = 'Type to search...',
  description,
  required,
  className,
}: FormAutoCompleteFieldProps<TFieldValues>) => {
  const items = React.useMemo(() => options.map((o) => ({ value: o, label: o })), [options]);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>
            {label} {required && <span className="text-primary">*</span>}
          </FormLabel>
          <FormControl>
            <AutoCompleteInner
              items={items}
              value={(field.value as string | undefined) ?? ''}
              onChange={field.onChange}
              placeholder={placeholder}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
FormAutoCompleteField.displayName = 'FormAutoCompleteField';
